import { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Alert,
  List, ListItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PublicIcon from '@mui/icons-material/Public';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getOverview } from '../../../services/securityhubApi';
import { engineById } from './engines/registry';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalEmptyState, PortalErrorState } from '../../../components/portal/PortalStates';
import EnableCapabilities from './EnableCapabilities';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { CK, FONT_BODY, listCardSx } from '../portalStyles';

// RegionsTab lists each enabled engine's home region. Regions are owned per
// engine (configured in each engine's own Settings), so this is read-only.
function RegionsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOverview();
      setRows((res?.data || []).filter((r) => r.summary?.enabled));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
      >
        Each detection engine manages its own regions in its Settings.
      </Alert>

      {error && <PortalErrorState error={error} onRetry={() => load()} />}

      {loading && <PortalLoading label="Loading engine regions…" />}

      {!loading && !error && rows.length === 0 && (
        <PortalEmptyState
          icon={<PublicIcon sx={{ fontSize: 28 }} />}
          title="No enabled engines"
          description="Turn on a security capability first — its home region will then appear here."
        />
      )}

      {!loading && !error && rows.length > 0 && (
        <List disablePadding>
          {rows.map((row, idx) => {
            const engine = engineById(row.engine);
            const homeRegion = row.summary?.configSummary?.homeRegion;
            const color = engine?.color || CK.blue;
            return (
              <Box key={row.engine}>
                {idx > 0 && <Divider />}
                <ListItem sx={{ px: 0, py: 1.5, gap: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36, color }}>
                    <PublicIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={engine?.label || row.engine}
                    secondary={homeRegion ? `Home region: ${homeRegion}` : 'No region configured'}
                    primaryTypographyProps={{
                      fontFamily: FONT_BODY,
                      fontWeight: 600,
                      color: CK.navy,
                    }}
                    secondaryTypographyProps={{
                      fontFamily: FONT_BODY,
                      fontSize: '0.8rem',
                      color: CK.muted,
                    }}
                  />
                </ListItem>
              </Box>
            );
          })}
        </List>
      )}
    </Box>
  );
}

// Configuration is the umbrella settings page: a Capabilities tab (reusing the
// EnableCapabilities panel) and a read-only Regions tab.
export default function Configuration() {
  const [tab, setTab] = useState(0);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<SettingsIcon sx={{ fontSize: 24 }} />}
          title="Configuration"
          subtitle="Security Hub capabilities and regions"
        />
      </motion.div>

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: `1px solid ${CK.border}` }}
          >
            <Tab label="Capabilities" />
            <Tab label="Regions" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {tab === 0 && <EnableCapabilities embedded />}
            {tab === 1 && <RegionsTab />}
          </Box>
        </Paper>
      </motion.div>
    </motion.div>
  );
}
